import React, { useEffect, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';
import { useFormContext } from 'react-hook-form';
import CancelModal from '../Owner/CancelModal';

/**
 * Prevents users from leaving the page with unsaved changes.
 * Shows a modal when the user tries to close/refresh the browser with unsaved changes.
 * Allows the user to proceed with navigation or cancel and stay on the current page.
 
 */
const NavigationHandler = ({
    bypassRef,
}: {
    /**
     * Set to `true` before navigating away once changes have been saved, so the
     * confirmation is skipped. This has to be a ref rather than a prop or form
     * state, because the blocker reads it while navigating, which happens before
     * React re-renders.
     */
    bypassRef?: React.RefObject<boolean>;
}) => {
    const {
        formState: { isDirty, isSubmitting },
    } = useFormContext();

    const [navigationBlockerOpen, setNavigationBlockerOpen] =
        useState<boolean>(false);

    // Block navigation when form has unsaved changes
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty &&
            !isSubmitting &&
            !bypassRef?.current &&
            currentLocation.pathname !== nextLocation.pathname,
    );

    // Warn user when trying to close/refresh browser with unsaved changes
    useBeforeUnload(
        React.useCallback(
            (event) => {
                if (isDirty && !isSubmitting && !bypassRef?.current) {
                    event.preventDefault();
                    return (event.returnValue = '');
                }
            },
            [isDirty, isSubmitting, bypassRef],
        ),
    );

    // Show modal when navigation is blocked
    useEffect(() => {
        if (blocker.state === 'blocked') {
            setNavigationBlockerOpen(true);
        }
    }, [blocker.state]);

    const handleProceedNavigation = () => {
        setNavigationBlockerOpen(false);
        blocker.proceed?.();
    };

    const handleCancelNavigation = () => {
        setNavigationBlockerOpen(false);
        blocker.reset?.();
    };

    return (
        <CancelModal
            open={navigationBlockerOpen}
            onClose={handleCancelNavigation}
            onSubmit={handleProceedNavigation}
        />
    );
};

export default NavigationHandler;
